import dayjs from "dayjs";
import forEach from "lodash/forEach";
import isEmpty from "lodash/isEmpty";
import set from "lodash/set";
import Utils from "../Utils";
import i18n from "../i18n";
import services from "./../services";
import appointment from "./appointment";

const strict = true;

const getDefaultState = () => {
  return {
    type: "request",
    appointment: {
      request: {
        toSearch: "",
        multiSelectConfig: {
          items: [
            "PENDING",
            "ACCEPTED",
            "ACCEPTED_POST",
            "DONE_NOTBILLABLE",
            "MEMBERCHANGE",
            "REQUESTCANCELLEDBYNEST",
            "APPOINTMENTCANCELLEDBYNEST",
            "NOTDONE",
            "ADMINISTRATIVE_CHECK_REQUIRED",
            "NOT_DELIVERABLE_BY_FE"
          ],
          activeItems: []
        },
        sortConfig: {
          descending: [true],
          sortBy: ["appointmentRequestDate"]
        },
        dateConfig: {
          range: {
            dateFrom: null,
            dateTo: null
          },
          dateToUseConfig: {
            active: "REQUEST",
            options: ["SCHEDULE", "REQUEST"]
          }
        },
        pagination: {
          page: 1,
          size: 15,
          isPageClicked: false
        },
        scheduling: null,
        acceptedToWork: null
      },
      booking: {
        toSearch: "",
        multiSelectConfig: {
          items: ["DONE"],
          activeItems: ["DONE"]
        },
        sortConfig: {
          descending: [false],
          sortBy: ["appointmentScheduleDate"]
        },
        dateConfig: {
          range: {
            dateFrom: null,
            dateTo: null
          },
          dateToUseConfig: {
            active: "SCHEDULE",
            options: ["SCHEDULE", "REQUEST"]
          }
        },
        pagination: {
          page: 1,
          size: 5,
          isPageClicked: false
        },
        scheduling: null,
        acceptedToWork: null
      }
    },
    filterCriteriaResults: "",
    appointmentList: [],
    activeAppointment: {},
    memberContactInfo: {},
    appointmentContactInfo: {},
    communications: [],
    totalAppointments: 0,
    scheduleSlot: {
      date: "",
      time: ""
    },
    bookableMonths: [],
    nextTwoBookableMonths: [],
    newBookableMonths: [],
    nextTwoNewBookableMonths: [],
    loadingBookableMonths: false,
    loadingServiceBookingInfo: false,
    cancelMotivation: {
      list: [],
      selected: "",
      cancelReason: "",
      motivationOtherId: "55097b6d-3f85-4d94-835d-4fe6b4dfb9ac"
    },
    serviceTimetableHashMap: {},
    serviceSlots: [],
    serviceSlotOffset: 0,
    serviceSlotDuration: {},
    memberCeiling: {},
    initScheduleCalendarOnMount: false,
    activeAppointmentService: {},
    serviceInfoScheduleCalendar: {},
    numOfAccepted: 0,
    numbOfToBeRescheduled: 0,
    numbOfMemberChange: 0,
    numbOfPending: 0,
    numbOfDone: 0,
    selectedAppointmentIds: []
  };
};

const state = getDefaultState();

const getters = {
  type: state => state.type,
  items: state => state.appointmentList,
  getAppointmentByIUP: state => iupCode => {
    let iupCodeUpperCase = iupCode.toUpperCase();
    return state.appointmentList.find(
      appointment => appointment.iupCode == iupCodeUpperCase
    );
  },
  toSearch: state => state.appointment[state.type].toSearch,
  multiSelectFullList: state =>
    state.appointment[state.type].multiSelectConfig.items,
  multiSelectActiveList: state =>
    state.appointment[state.type].multiSelectConfig.activeItems,
  order: state => state.appointment[state.type].sortConfig,
  dateRange: state => state.appointment[state.type].dateConfig.range,
  dateToUseOptions: state =>
    state.appointment[state.type].dateConfig.dateToUseConfig.options,
  dateToUse: state =>
    state.appointment[state.type].dateConfig.dateToUseConfig.active,
  page: state => state.appointment[state.type].pagination.page,
  totalPages: state => {
    return Math.ceil(
      state.totalAppointments / state.appointment[state.type].pagination.size
    );
  },
  size: state => state.appointment[state.type].pagination.size,
  filterCriteriaResults: state => state.filterCriteriaResults,
  totalItems: state => state.totalAppointments,
  appointmentList: state => state.appointmentList,
  scheduleSlot: state => state.scheduleSlot,
  scheduleDate: state => state.scheduleSlot.date,
  scheduleTime: state => state.scheduleSlot.time,
  activeAppointment: state => state.activeAppointment,
  activeAppointmentId: state => state.activeAppointment.appointmentId,
  memberContactInfo: state => state.memberContactInfo,
  appointmentContactInfo: state => state.appointmentContactInfo,
  communications: state => state.communications,
  bookableMonths: state => state.bookableMonths,
  nextTwoBookableMonths: state => state.nextTwoBookableMonths,
  newBookableMonths: state => state.newBookableMonths,
  nextTwoNewBookableMonths: state => state.nextTwoNewBookableMonths,
  loadingBookableMonths: state => state.loadingBookableMonths,
  loadingServiceBookingInfo: state => state.loadingServiceBookingInfo,
  cancelMotivationList: state => state.cancelMotivation.list,
  cancelMotivationSelectedItem: state => state.cancelMotivation.selected,
  cancelReason: state => state.cancelMotivation.cancelReason,
  cancelMotivationOtherId: state => state.cancelMotivation.motivationOtherId,
  serviceTimetableHashMap: state => state.serviceTimetableHashMap,
  serviceSlots: state => state.serviceSlots,
  serviceSlotOffset: state => state.serviceSlotOffset,
  serviceSlotDuration: state => state.serviceSlotDuration,
  memberLiquidated: state => state.memberCeiling.liquidated,
  memberReserved: state => state.memberCeiling.reserved,
  memberResidualCeiling: state => state.memberCeiling.residualCeiling,
  initScheduleCalendarOnMount: state => state.initScheduleCalendarOnMount,
  activeAppointmentService: state => state.activeAppointmentService,
  serviceInfoScheduleCalendar: state => state.serviceInfoScheduleCalendar,
  getNumOfAccepted: state => state.numOfAccepted,
  getNumbOfToBeRescheduled: state => state.numbOfToBeRescheduled,
  getNumbOfMemberChange: state => state.numbOfMemberChange,
  getNumbOfPending: state => state.numbOfPending,
  getNumbOfDone: state => state.numbOfDone,
  getSelectedAppointmentIds: state => state.selectedAppointmentIds
};

const mutations = {
  RESET(state) {
    // acquire initial state
    let s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  setType(state, type) {
    state.type = type;
  },
  SET_TO_SEARCH(state, toSearch) {
    state.appointment[state.type].toSearch = toSearch;
    state.appointment[state.type].pagination.isPageClicked = false;
  },
  SET_MULTI_SELECT_ACTIVE_LIST(state, multiSelectList) {
    let allAppuntamentiCancellati = [
      "APPOINTMENTCANCELLEDBYNEST",
      "APPOINTMENTCANCELLEDBYMEMBER"
    ];
    let allRichiesteCancellate = [
      "REQUESTCANCELLEDBYNEST",
      "REQUESTCANCELLEDBYMEMBER",
      "REQUESTCANCELLEDBYPORTAL",
      "APPOINTMENTCANCELLEDBYPORTAL",
      "APPOINTMENTCANCELLEDCONFIRMED"
    ];
    if (
      multiSelectList.includes("APPOINTMENTCANCELLEDBYNEST") &&
      !multiSelectList.includes("APPOINTMENTCANCELLEDBYMEMBER")
    ) {
      multiSelectList.splice(
        multiSelectList.indexOf("APPOINTMENTCANCELLEDBYNEST"),
        1,
        ...allAppuntamentiCancellati
      );
    }
    if (
      multiSelectList.includes("REQUESTCANCELLEDBYNEST") &&
      !multiSelectList.includes("REQUESTCANCELLEDBYMEMBER")
    ) {
      multiSelectList.splice(
        multiSelectList.indexOf("REQUESTCANCELLEDBYNEST"),
        1,
        ...allRichiesteCancellate
      );
    }
    state.appointment[state.type].multiSelectConfig.activeItems =
      multiSelectList;
    state.appointment[state.type].pagination.isPageClicked = false;
  },
  REMOVE_MULTI_SELECT_ITEM(state, item) {
    let activeItems =
      state.appointment[state.type].multiSelectConfig.activeItems;
    if (item === "APPOINTMENTCANCELLEDBYNEST") {
      activeItems.splice(activeItems.indexOf("APPOINTMENTCANCELLEDBYNEST"), 1);
      activeItems.splice(
        activeItems.indexOf("APPOINTMENTCANCELLEDBYMEMBER"),
        1
      );
    } else if (item === "REQUESTCANCELLEDBYNEST") {
      activeItems.splice(activeItems.indexOf("REQUESTCANCELLEDBYNEST"), 1);
      activeItems.splice(activeItems.indexOf("REQUESTCANCELLEDBYMEMBER"), 1);
      activeItems.splice(activeItems.indexOf("REQUESTCANCELLEDBYPORTAL"), 1);
      activeItems.splice(
        activeItems.indexOf("APPOINTMENTCANCELLEDBYPORTAL"),
        1
      );
      activeItems.splice(
        activeItems.indexOf("APPOINTMENTCANCELLEDCONFIRMED"),
        1
      );
    } else {
      let index = activeItems.findIndex(i => i === item);
      activeItems.splice(index, 1);
    }
  },
  SET_ORDER(state, order) {
    state.appointment[state.type].sortConfig = order;
  },
  updateDateRange(state, dateRange) {
    state.appointment[state.type].dateConfig.range = dateRange;
    state.appointment[state.type].pagination.isPageClicked = false;
  },
  updateDateToUse(state, dateToUse) {
    state.appointment[state.type].dateConfig.dateToUseConfig.active = dateToUse;
  },
  updatePage(state, page) {
    state.appointment[state.type].pagination.page = page;
    state.appointment[state.type].pagination.isPageClicked = true;
  },
  updateSize(state, size) {
    state.appointment[state.type].pagination.size = size;
  },
  setIsPageClicked(state, isPageClicked) {
    state.appointment[state.type].pagination.isPageClicked = isPageClicked;
  },
  setTotalAppointments(state, totalAppointments) {
    state.totalAppointments = totalAppointments;
  },
  udpateAcceptedToWork(state, acceptedToWork) {
    state.appointment[state.type].acceptedToWork = acceptedToWork;
  },
  SET_FILTER_CRITERIA_RESULTS(state) {
    let criteria = [];
    if (state.appointment[state.type].toSearch) {
      criteria.push("'" + state.appointment[state.type].toSearch + "'");
    }
    let activeMultiSelectItems =
      state.appointment[state.type].multiSelectConfig.activeItems;
    if (!isEmpty(activeMultiSelectItems)) {
      let allAppuntamentiCancellati = [
        "APPOINTMENTCANCELLEDBYNEST",
        "APPOINTMENTCANCELLEDBYMEMBER"
      ];
      let allRichiesteCancellate = [
        "REQUESTCANCELLEDBYNEST",
        "REQUESTCANCELLEDBYMEMBER",
        "REQUESTCANCELLEDBYPORTAL",
        "APPOINTMENTCANCELLEDBYPORTAL",
        "APPOINTMENTCANCELLEDCONFIRMED"
      ];
      let translatedActiveItems = [];
      forEach(activeMultiSelectItems, item => {
        if (allAppuntamentiCancellati.includes(item)) {
          translatedActiveItems.push(
            i18n
              .t(`baseFilters.appointments.APPOINTMENTCANCELLEDBYNESTCheckbox`)
              .toUpperCase()
          );
        } else if (allRichiesteCancellate.includes(item)) {
          translatedActiveItems.push(
            i18n
              .t(`baseFilters.appointments.REQUESTCANCELLEDBYNESTCheckbox`)
              .toUpperCase()
          );
        } else {
          translatedActiveItems.push(
            i18n.t(`baseFilters.appointments.${item}Checkbox`).toUpperCase()
          );
        }
      });
      let uniqueTranslatedActiveItems = [...new Set(translatedActiveItems)];
      criteria.push("'" + uniqueTranslatedActiveItems.join(", ") + "'");
    }
    let dateFrom = state.appointment[state.type].dateConfig.range.dateFrom;
    let dateTo = state.appointment[state.type].dateConfig.range.dateTo;
    if (dateFrom || dateTo) {
      let formatDateFrom = dateFrom ? dayjs(dateFrom).format("DD/MM/YYYY") : "";
      let formatDateTo = dateTo ? dayjs(dateTo).format("DD/MM/YYYY") : "";
      if (state.type === "booking") {
        let dateToUse =
          state.appointment[state.type].dateConfig.dateToUseConfig.active;
        criteria.push(
          "'" +
            formatDateFrom +
            " - " +
            formatDateTo +
            " (" +
            i18n.t(`baseFilters.datePicker.dateToUse.${dateToUse}`) +
            ")" +
            "'"
        );
      } else {
        criteria.push("'" + formatDateFrom + " - " + formatDateTo + "'");
      }
    }
    state.filterCriteriaResults = criteria.join(" & ");
  },
  setAppointments(state, appointmentList) {
    for (let appointment of appointmentList) {
      if (appointment.state == "ACCEPTED" && !appointment.acceptedToWork) {
        appointment.state = "ACCEPTED_POST";
      }
    }
    state.appointmentList = appointmentList;
  },
  setActiveAppointment(state, appointment) {
    state.activeAppointment = appointment;
  },
  contactInfo(state, contactInfo) {
    let mci = {
      memberContactingChannel: contactInfo.memberContactingChannel,
      memberPreferredContactType: contactInfo.memberPreferredContactType,
      memberVoucherChannelType: contactInfo.memberVoucherChannelType
    };
    let aci = {
      appointmentContactingChannel: contactInfo.appointmentContactingChannel,
      appointmentPreferredContactType:
        contactInfo.appointmentPreferredContactType,
      appointmentVoucherChannelType: contactInfo.appointmentVoucherChannelType
    };
    state.memberContactInfo = mci;
    state.appointmentContactInfo = aci;
    state.communicatupdatePagcommitions = contactInfo.communications;
  },
  setScheduleDate(state, date) {
    state.scheduleSlot.date = date;
  },
  setScheduleTime(state, time) {
    state.scheduleSlot.time = time;
  },
  resetScheduleSlot(state) {
    state.scheduleSlot = {
      date: "",
      time: ""
    };
  },
  setAppointmentStatus(state, appointmentStatus) {
    state.activeAppointment.state = appointmentStatus;
  },
  setIUPCode(state, iupCode) {
    state.activeAppointment.iupCode = iupCode;
  },
  setServiceId(state, serviceId) {
    state.activeAppointment.serviceId = serviceId;
  },
  setServiceName(state, serviceName) {
    state.activeAppointment.serviceName = serviceName;
  },
  setAppointmentScheduleDate(state, appointmentScheduleDate) {
    state.activeAppointment.appointmentScheduleDate = appointmentScheduleDate;
  },
  setBookableMonths(state, bookableMonths) {
    const maxMonth = dayjs().add(1, "month");
    const availableBm = bookableMonths.filter(m =>
      dayjs(`${m.year}-${m.month}-01`).isSameOrBefore(maxMonth, "month")
    );
    state.bookableMonths = bookableMonths;
    state.nextTwoBookableMonths = availableBm;
  },
  setNewBookableMonths(state, newBookableMonths) {
    const maxMonth = dayjs().add(1, "month");
    const availableBm = newBookableMonths.filter(m =>
      dayjs(`${m.year}-${m.month}-01`).isSameOrBefore(maxMonth, "month")
    );
    state.newBookableMonths = newBookableMonths;
    state.nextTwoNewBookableMonths = availableBm;
  },
  setLoadingBookableMonths(state, value) {
    state.loadingBookableMonths = value;
  },
  setLoadingServiceBookingInfo(state, value) {
    state.loadingServiceBookingInfo = value;
  },
  setCancelMotivationList(state, cancelMotivationList) {
    let sorted = cancelMotivationList.sort((a, b) => {
      let titleA = a?.title?.it_IT.toLowerCase();
      let titleB = b?.title?.it_IT.toLowerCase();
      // "Altro" sempre in fondo
      if (titleA === "altro") return 1;
      if (titleB === "altro") return -1;
      // Applica ordinamento alfabetico
      return titleA.localeCompare(titleB, "it-IT");
    });
    state.cancelMotivation.list = sorted;
  },
  setCancelMotivationSelectedItem(state, selectedItem) {
    state.cancelMotivation.selected = selectedItem;
  },
  setCancelReason(state, reason) {
    state.cancelMotivation.cancelReason = reason;
  },
  resetCancelMotivation(state) {
    state.cancelMotivation.selected = "";
    state.cancelMotivation.cancelReason = "";
  },
  setServiceTimetableHashMap(state, serviceTimetableHashMap) {
    state.serviceTimetableHashMap = serviceTimetableHashMap;
  },
  setServiceSlotDuration(state, duration) {
    state.serviceSlotDuration = duration;
  },
  setServiceSlots(state, serviceSlots) {
    let sss = serviceSlots.map(slot => {
      let date = dayjs(slot.dateTime);
      return {
        dateTime: date.toDate().toISOString(),
        date: date.format("YYYY-MM-DD"),
        time: date.format("HH:mm"),
        to: date.add(state.serviceSlotDuration.value, "minute").format("HH:mm"),
        duration: state.serviceSlotDuration.value,
        doctorId: slot.doctorId,
        isSelected: false
      };
    });
    state.serviceSlots = sss;
  },
  addServiceSlotsBasedOnAppointmentDate(state) {
    let appScheduleDate = state.activeAppointment.appointmentScheduleDate;
    let isAppScheduleDateInTheFuture = dayjs(appScheduleDate).isAfter(
      dayjs(),
      "date"
    );
    let bm = [...state.newBookableMonths];
    let newSlots = [];
    let start = isAppScheduleDateInTheFuture ? dayjs() : dayjs(appScheduleDate);
    let end = dayjs().add(2, "day");
    let loop = dayjs(start);
    while (
      dayjs(loop).isSameOrBefore(dayjs(end).format("YYYY-MM-DD"), "date")
    ) {
      let d = dayjs(loop).format("YYYY-MM-DD");
      let dateIsInABookableMonth = bm.find(
        month =>
          dayjs(d).month() + 1 === month.month && dayjs(d).year() === month.year
      );
      if (dateIsInABookableMonth) {
        let slot = {
          date: d,
          dateTime: dayjs(d).set("hour", 12).toISOString(),
          doctorId: null,
          duration: 30,
          isSelected: false,
          time: "12:00",
          to: "12:30"
        };
        newSlots.push(slot);
      }
      let nextDate = dayjs(loop).add(1, "day");
      loop = dayjs(nextDate);
    }
    let existingSlots = state.serviceSlots;
    let totalSlots = [...existingSlots, ...newSlots];
    state.serviceSlots = totalSlots;
  },
  setServiceSlotOffset(state, offset) {
    state.serviceSlotOffset = offset;
  },
  updateSelectedServiceSlot(state, slot) {
    if (slot) {
      let s1 = dayjs(slot.dateTime).toDate().toISOString();
      state.serviceSlots.forEach(d => {
        let d1 = dayjs(d.dateTime).toDate().toISOString();
        if (d1 === s1) {
          d.isSelected = true;
        } else {
          d.isSelected = false;
        }
      });
    } else {
      state.serviceSlots.forEach(d => {
        d.isSelected = false;
      });
    }
  },
  setMemberCeiling(state, memberCeiling) {
    state.memberCeiling = memberCeiling;
  },
  setInitScheduleCalendarOnMount(state, status) {
    state.initScheduleCalendarOnMount = status;
  },
  setActiveAppointmentService(state, service) {
    state.activeAppointmentService = service;
  },
  setServiceInfoScheduleCalendar(state, service) {
    let payload = {
      serviceId: service.serviceId,
      serviceType: service.bookingType,
      officeServiceId: service.officeServiceId
    };
    state.serviceInfoScheduleCalendar = payload;
  },
  setAppointmentsAcceptedCount(state, count) {
    state.numOfAccepted = count;
  },
  setAppointmentsPendigCount(state, count) {
    state.numbOfPending = count;
  },
  setAppointmentsMemberChangeCount(state, count) {
    state.numbOfMemberChange = count;
  },
  setAppointmentsToBeRescheduledCount(state, count) {
    state.numbOfToBeRescheduled = count;
  },
  setAppointmentsDoneCount(state, count) {
    state.numbOfDone = count;
  },
  addIdToSelectedAppointmentIds(state, id) {
    state.selectedAppointmentIds.push(id);
  },
  removeIdFromSelectedAppointmentIds(state, i) {
    state.selectedAppointmentIds.splice(i, 1);
  },
  clearSelectedAppointmentsIds(state) {
    state.selectedAppointmentIds = [];
  }
};

const actions = {
  getItems({ commit, state }, officeId) {
    if (state.appointment[state.type].pagination.isPageClicked === false) {
      commit("updatePage", 1);
    }
    return services.appointmentsService
      .fetchAppointments(
        officeId,
        constructPayloadToFetchAppointments(state.appointment[state.type])
      )
      .then(appointmentObj => {
        let numberOfAppointments =
          appointmentObj.numberOfAppointments === null
            ? 0
            : appointmentObj.numberOfAppointments;
        commit("setIsPageClicked", null);
        if (
          numberOfAppointments > 0 &&
          appointmentObj.appointmentList.length === 0
        ) {
          commit("updatePage", 1);
          return services.appointmentsService
            .fetchAppointments(
              officeId,
              constructPayloadToFetchAppointments(state.appointment[state.type])
            )
            .then(appointmentObjSecondTry => {
              numberOfAppointments =
                appointmentObjSecondTry.numberOfAppointments === null
                  ? 0
                  : appointmentObjSecondTry.numberOfAppointments;
              commit(
                "setAppointments",
                appointmentObjSecondTry.appointmentList
              );
              commit("setTotalAppointments", numberOfAppointments);
            })
            .catch(error => {
              return Promise.reject(error);
            });
        } else {
          commit("setAppointments", appointmentObj.appointmentList);
          commit("setTotalAppointments", numberOfAppointments);
        }
      })
      .catch(error => {
        commit("setAppointments", []);
        commit("setTotalAppointments", 0);
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {
        commit("SET_FILTER_CRITERIA_RESULTS");
      });
  },
  fetchAppointmentsByIUP({ commit }, payload) {
    return services.appointmentsService
      .fetchAppointmentsByIUP(payload)
      .then(appointmentObj => {
        return appointmentObj.appointmentId;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.noIUPCodeFound.title";
          errorDialogDetails.msg = "appointments.errors.noIUPCodeFound.msg";
          errorDialogDetails.actionBtn =
            "appointments.errors.noIUPCodeFound.btn";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async fetchAppointmentById({ commit, dispatch }, parameters) {
    try {
      let appointment = await services.appointmentsService.fetchAppointmentById(
        parameters
      );
      commit("setActiveAppointment", appointment);
      let appointmentId = appointment?.appointmentId;
      let appState = appointment?.state;
      if (["DONE", "INBILL"].includes(appState)) {
        await dispatch("bills/getProvidedServiceById", appointmentId, {
          root: true
        });
      }
      let contactInfo =
        await services.appointmentsService.callGetMemberContactInfo(
          appointmentId
        );
      commit("contactInfo", contactInfo);
      if (["PENDING", "MEMBERCHANGE", "TOBERESCHEDULED"].includes(appState)) {
        let bookingInfoParams = {
          officeId: appointment?.hfOfficeId,
          serviceId: appointment?.serviceId,
          memberFiscalCode: appointment?.memberCodiceFiscale
        };
        await dispatch("getServiceBookingInfo", bookingInfoParams);
      }
    } catch (error) {
      commit("setActiveAppointment", {});
      commit("contactInfo", {});
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "appointments";
      } else if (error.type == Utils.errorTypes.CUSTOM) {
        errorDialogDetails.title = "appointments.errors.412.title";
        errorDialogDetails.msg = "appointments.errors.412.msg";
        errorDialogDetails.actionBtn = "appointments.errors.412.btn";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
    }
  },
  getServiceBookingInfo({ commit }, parameters) {
    commit("setLoadingServiceBookingInfo", true);
    return services.bookingsService
      .callGetServiceBookingInfo(parameters)
      .then(bookingInfo => {
        let bookableMonths = [];
        let yearCoverage;
        let thisYear = dayjs().year();
        let currentYearCeiling = {};
        for (let [year, yearInfo] of Object.entries(bookingInfo)) {
          if (year == thisYear) {
            currentYearCeiling = yearInfo.residualCeiling;
            commit("setMemberCeiling", currentYearCeiling);
          }
          yearCoverage = yearInfo.coverage;
          if (yearCoverage.length) {
            yearCoverage = yearCoverage.filter(
              coverage => coverage.bookableByRule && !coverage.notCovered
            );
            bookableMonths.push(
              ...yearCoverage.map(coverage => ({
                year,
                month: coverage.month
              }))
            );
          }
        }
        return bookableMonths;
      })
      .then(bookableMonths => {
        commit("setBookableMonths", bookableMonths);
      })
      .catch(error => {
        console.log(error);
        commit("setBookableMonths", []);
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {
        commit("setLoadingServiceBookingInfo", false);
      });
  },
  async resetBookableMonthsAndSlotPropertiesOfAppointmentWizard({ commit }) {
    await commit("setNewBookableMonths", []);
    await commit("setLoadingBookableMonths", false);
    await commit("setServiceSlotDuration", {});
    await commit("setServiceSlotOffset", 0);
    await commit("setServiceSlots", []);
  },
  /**
   * New bookingInfo service that simulating the removal of the given appointment ceiling data.
   * This service is used both for:
   *  - edit appointment service (using the new serviceId selected during the wizard);
   *  - edit appointment date (using the same serviceId of the given appointment);
   */
  async getNewServiceBookingInfo(
    { commit, getters, rootGetters },
    useNewServiceId
  ) {
    commit("setLoadingBookableMonths", true);
    // fondoest/issues#761 -> fondoest/issues#1370
    let appScheduleDate = getters["activeAppointment"]?.appointmentScheduleDate;
    let useAppointmentDate = !appScheduleDate
      ? false
      : dayjs(appScheduleDate).isSameOrBefore(dayjs(), "month");
    let appointmentId = getters["activeAppointment"].appointmentId;
    let officeId = getters["activeAppointment"].hfOfficeId;
    let serviceId = getters["activeAppointment"].serviceId;
    let newServiceId = rootGetters["appointment/getService"].serviceId;
    let memberFiscalCode = getters["activeAppointment"].memberCodiceFiscale;
    let parameters = {
      officeId: officeId,
      appointmentId: appointmentId,
      serviceId: useNewServiceId ? newServiceId : serviceId,
      memberFiscalCode: memberFiscalCode,
      useAppointmentDate: useAppointmentDate
    };
    commit("setNewBookableMonths", []);
    return await services.bookingsService
      .callGetNewServiceBookingInfo(parameters)
      .then(bookingInfo => {
        let newBookableMonths = [];
        let yearCoverage;
        for (let [y, yearInfo] of Object.entries(bookingInfo)) {
          yearCoverage = yearInfo.coverage;
          if (yearCoverage.length) {
            yearCoverage = yearCoverage.filter(
              m =>
                m.bookableByRule &&
                !m.notCovered &&
                !m.maxCeilingReached &&
                !m.maxHealthCareReached
            );
            let year = parseInt(y);
            newBookableMonths.push(
              ...yearCoverage.map(m => ({
                year,
                month: m.month,
                isBookable:
                  m.bookableByRule &&
                  !m.notCovered &&
                  !m.maxCeilingReached &&
                  !m.maxHealthCareReached
                    ? true
                    : false,
                ...m
              }))
            );
          }
        }
        return newBookableMonths;
      })
      .then(newBookableMonths => {
        let bookableMonthsGroupedByCodicePacchetto =
          groupBookableMonthsByCodicePacchetto(newBookableMonths);
        let isSanimpresaOffice = rootGetters["hfManagement/isSanimpresaOffice"];
        if (isSanimpresaOffice) {
          // filter bookable months with only months that are bookable and have codicePacchetto "SANI"
          let saniBookableMonths =
            bookableMonthsGroupedByCodicePacchetto.filter(
              month =>
                month.isBookable && month.codicePacchetto.includes("SANI")
            );
          commit("setNewBookableMonths", saniBookableMonths);
        } else {
          // filter bookable months with only months that are bookable
          commit(
            "setNewBookableMonths",
            bookableMonthsGroupedByCodicePacchetto.filter(
              month => month.isBookable
            )
          );
        }
      })
      .catch(error => {
        console.log(error);
        commit("setNewBookableMonths", []);
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {
        commit("setLoadingBookableMonths", false);
      });
  },
  async getServiceById({ commit, getters }) {
    commit("setActiveAppointmentService", {});
    let officeId = getters["activeAppointment"].hfOfficeId;
    let serviceId = getters["activeAppointment"].serviceId;
    let ancillaries = getters["activeAppointment"]?.hAncillaries || [];
    ancillaries = ancillaries.map(a => ({ ancillaryId: a.id, num: a.num }));
    return await services.bookingsService
      .getServiceWithId({
        officeId: officeId,
        serviceId: serviceId,
        ancillaryIds: ancillaries
      })
      .then(service => {
        commit("setActiveAppointmentService", service);
        return service;
      })
      .catch(error => {
        console.log(error);
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async initServiceInfo({ commit }, service) {
    commit("setServiceInfoScheduleCalendar", service);
  },
  getServiceTimetable({ commit, getters }, { serviceId }) {
    let officeId = getters["activeAppointment"].hfOfficeId;
    return services.hfManagementService
      .fetchServiceTimetable(officeId, serviceId)
      .then(serviceTimetableList => {
        let serviceTimetableHashMap = serviceTimetableList.reduce(
          (obj, item) => {
            if (!obj[item["day"]]) {
              obj[item["day"]] = [item];
            } else {
              obj[item["day"]].push(item);
            }
            return obj;
          },
          {}
        );
        commit("setServiceTimetableHashMap", serviceTimetableHashMap);
      })
      .catch(error => {
        console.log(error);
        commit("setServiceTimetableHashMap", {});
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async getServiceSlots({ commit, getters }, monthOffset) {
    try {
      let res;
      let nextAvailableMonth;
      do {
        res = await services.bookingsService.getSlotsForMonths({
          id: getters["activeAppointment"].hfOfficeId,
          monthOffset: monthOffset,
          officeserviceId:
            getters["serviceInfoScheduleCalendar"].officeServiceId
        });
        if (res?.slotList && res?.slotList?.length === 0) {
          let bookableMonths = getters["newBookableMonths"];
          let thisMonth = dayjs().set("date", 1);
          let requestedMonth = thisMonth.add(monthOffset, "month");
          nextAvailableMonth = bookableMonths.find(m =>
            requestedMonth.isBefore(dayjs(`${m.year}-${m.month}-01`))
          );
          monthOffset = nextAvailableMonth
            ? Math.abs(
                dayjs(
                  `${nextAvailableMonth.year}-${nextAvailableMonth.month}-01`
                ).month() - thisMonth.month()
              )
            : monthOffset;
          commit("setServiceSlots", []);
        } else {
          commit("setServiceSlotOffset", monthOffset);
          commit("setServiceSlots", res?.slotList);
          commit("setServiceSlotDuration", res?.duration?.value);
          // fondoest/issues#761
          commit("addServiceSlotsBasedOnAppointmentDate");
        }
      } while (res.slotList.length === 0 && nextAvailableMonth !== undefined);
    } catch (error) {
      commit("setServiceSlotOffset", 0);
      commit("setServiceSlotDuration", {});
      commit("setServiceSlots", []);
      let errorDialogDetails = {
        ...error
      };
      if (error.type == Utils.errorTypes.STATUS) {
        errorDialogDetails.dataName = "appointments";
      } else if (error.type == Utils.errorTypes.CUSTOM) {
        errorDialogDetails.title = "appointments.errors.412.title";
        errorDialogDetails.msg = "appointments.errors.412.done.msg";
        errorDialogDetails.actionBtn = "appointments.errors.412.btn";
      }
      commit("error/SET_ERROR", errorDialogDetails, {
        root: true
      });
      return Promise.reject();
    }
  },
  fetchRejectReasons({ commit }) {
    let REJECT_REASON_TYPE = "APPOINTMENT_CANCELED_BYHFPORTAL";
    return services.appointmentsService
      .fetchRejectReasons(REJECT_REASON_TYPE)
      .then(cancelMotivationList => {
        commit("setCancelMotivationList", cancelMotivationList);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  /* CHANGE APPOINTMENT STATE/DATE */
  cancelAppointment({ getters, commit }) {
    let appointmentId = getters["activeAppointmentId"];
    let cancelMotivationOtherId = getters["cancelMotivationOtherId"];

    let cancelReasonId = getters["cancelMotivationSelectedItem"];
    let cancelReasonAdditionalInfo =
      cancelReasonId === cancelMotivationOtherId ? getters["cancelReason"] : "";

    let payload = {
      cancelReasonId,
      cancelReasonAdditionalInfo
    };

    return services.appointmentsService
      .cancelAppointment(appointmentId, payload)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.cancel.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  cancelConfirmAppointment({ getters, commit }) {
    let appointmentId = getters["activeAppointmentId"];
    return services.appointmentsService
      .cancelConfirmAppointment(appointmentId)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.cancelConfirm.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async callVerifyAppointmentDeliveryDate({ rootGetters, commit }, payload) {
    let officeId = rootGetters["hfManagement/officeId"];
    payload["officeId"] = officeId;
    let appointments = {};
    for (let obj of payload.obj.dates) {
      if (Object.keys(obj.appointment).length > 0) {
        appointments[obj.appointmentId] = obj.appointment;
      }
    }
    return await services.appointmentsService
      .verifyAppointmentDeliveryDate(payload)
      .then(response => {
        return getVerifyAppointmentDeliveryDateResult(response, appointments);
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.done.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async callSetAppointmentToDone({ commit }, params) {
    let payload = {
      id: params.appId,
      deliveryDate: dayjs(`${params.appDeliveryDate}T12:00`).format()
    };
    return await services.appointmentsService
      .setAppointmentToDone(payload)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.done.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async callSetAppointmentToAdministrativeCheckRequired({ commit }, params) {
    let payload = { id: params.appId };
    payload["obj"] = {
      appointmentDeliveryDate: params.appDeliveryDate.includes("T")
        ? params.appDeliveryDate
        : dayjs(`${params.appDeliveryDate}T12:00`).format()
    };
    return await services.appointmentsService
      .setAppointmentToAdministrativeCheckRequired(payload)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.done.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  callSetAppointmentToNotDone({ commit }, appointmentId) {
    return services.appointmentsService
      .setAppointmentToNotDone(appointmentId)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.done.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  setAppointmentNoImpact({ getters, commit }) {
    let appointmentId = getters["activeAppointmentId"];
    return services.appointmentsService
      .setAppointmentNoImpact(appointmentId)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        } else if (error.type == Utils.errorTypes.CUSTOM) {
          errorDialogDetails.title = "appointments.errors.412.title";
          errorDialogDetails.msg = "appointments.errors.412.done.msg";
          errorDialogDetails.actionBtn = "appointments.errors.412.btn";
        }

        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  setAppointmentDate({ getters, commit }) {
    let appointmentId = getters["activeAppointmentId"];
    let scheduleSlot = getters["scheduleSlot"];
    let dateAndTime = dayjs(
      `${scheduleSlot.date}T${scheduleSlot.time}`
    ).format();
    let payload = {
      appointmentId,
      dateAndTime
    };
    return services.appointmentsService
      .setAppointmentDate(payload)
      .then(() => {})
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  modifyExpostAppointmentDate({ commit, getters }) {
    let id = getters["activeAppointmentId"];
    let scheduleSlot = getters["scheduleSlot"];
    let dateAndTime = dayjs(
      `${scheduleSlot.date}T${scheduleSlot.time}`
    ).format();
    return services.appointmentsService
      .modifyExpostAppointmentDate(id, dateAndTime)
      .then(replacedAppointmentInfo => {
        return replacedAppointmentInfo;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  modifySlotAppointmentDate({ commit, getters }) {
    let id = getters["activeAppointmentId"];
    let scheduleSlot = getters["scheduleSlot"];
    let dateAndTime = dayjs(`${scheduleSlot.date} ${scheduleSlot.time}`)
      .toDate()
      .toISOString();
    return services.appointmentsService
      .modifySlotAppointmentDate(id, dateAndTime)
      .then(replacedAppointmentInfo => {
        return replacedAppointmentInfo;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  modifyTimeperiodAppointmentDate({ commit, getters }) {
    let id = getters["activeAppointmentId"];
    let scheduleSlot = getters["scheduleSlot"];
    let dateAndTime = dayjs(
      `${scheduleSlot.date}T${scheduleSlot.time}`
    ).format();
    return services.appointmentsService
      .modifyTimeperiodAppointmentDate(id, dateAndTime)
      .then(replacedAppointmentInfo => {
        return replacedAppointmentInfo;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  downloadVoucher({ commit }, payload) {
    return services.appointmentsService
      .callDownloadVoucherDocument(payload)
      .then(appointmentDoc => {
        return appointmentDoc;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  downloadFascicolo({ commit }, payload) {
    return services.appointmentsService
      .callDownloadFascicoloDocument(payload)
      .then(fascicoloDoc => {
        return fascicoloDoc;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  downloadPrescription({ commit }, payload) {
    return services.appointmentsService
      .callDownloadPrescriptionDocument(payload)
      .then(prescriptionDoc => {
        return prescriptionDoc;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  downloadInvoice({ commit }, payload) {
    return services.appointmentsService
      .callDownloadInvoiceDocument(payload)
      .then(invoiceDoc => {
        return invoiceDoc;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  getAppointmentsCount({ commit, rootGetters }, states) {
    let officeId = rootGetters["hfManagement/officeId"];
    let payload = { states: states };
    return services.appointmentsService
      .getAppointmentsCountByStates(officeId, payload)
      .then(response => {
        const statesArray = response.body.response || [];
        // ciclo per ogni stato
        statesArray.forEach(item => {
          switch (item.state) {
            case "PENDING":
              commit("setAppointmentsPendigCount", item.count);
              break;
            case "ACCEPTED":
              commit("setAppointmentsAcceptedCount", item.count);
              break;
            case "TOBERESCHEDULED":
              commit("setAppointmentsToBeRescheduledCount", item.count);
              break;
            case "MEMBERCHANGE":
              commit("setAppointmentsMemberChangeCount", item.count);
              break;
            case "DONE":
              commit("setAppointmentsDoneCount", item.count);
              break;
            default:
              break;
          }
        });
        return response;
      })
      .catch(error => {
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "appointments";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
        return Promise.reject();
      })
      .finally(() => {});
  },
  async addSelectedAppointmentToBills({ state, commit, dispatch }) {
    const ids = state.selectedAppointmentIds;
    if (ids.length !== 0) {
      for (const id of ids) {
        await dispatch("billAppointments/addDoneAppointment", id, {
          root: true
        });
      }
    }
    //clear the list
    commit("clearSelectedAppointmentsIds");
  }
};

function constructPayloadToFetchAppointments(appointmentMode) {
  let order;
  let orderField;
  let dateToUse = appointmentMode.dateConfig.dateToUseConfig.active;
  let page;
  let size;
  let state;
  let dateFrom;
  let dateTo;
  let toSearch;
  let scheduling;
  let acceptedToWork = appointmentMode.acceptedToWork;

  if (appointmentMode.sortConfig.sortBy) {
    order = appointmentMode.sortConfig.descending[0] ? "DESC" : "ASC";
    orderField = appointmentMode.sortConfig.sortBy[0];
  }
  if (appointmentMode.pagination.page)
    page = appointmentMode.pagination.page - 1;

  if (appointmentMode.pagination.size) size = appointmentMode.pagination.size;

  let statesList =
    appointmentMode.multiSelectConfig.activeItems.length > 0
      ? [...appointmentMode.multiSelectConfig.activeItems]
      : [...appointmentMode.multiSelectConfig.items];
  // Normalize states: handle ACCEPTED/ACCEPTED_POST consistency and set acceptedToWork flag.
  if (statesList.includes("ACCEPTED") && statesList.includes("ACCEPTED_POST")) {
    statesList = statesList.filter(s => s !== "ACCEPTED_POST");
    acceptedToWork = null;
  } else if (statesList.includes("ACCEPTED")) {
    acceptedToWork = true;
  } else if (statesList.includes("ACCEPTED_POST")) {
    acceptedToWork = false;
    statesList = statesList
      .filter(s => s !== "ACCEPTED_POST")
      .concat("ACCEPTED");
  } else {
    statesList = statesList.filter(s => s !== "ACCEPTED_POST");
    acceptedToWork = null;
  }

  state = statesList.join(",");

  if (appointmentMode.dateConfig.range.dateFrom)
    dateFrom = dayjs(appointmentMode.dateConfig.range.dateFrom).format(
      "DD/MM/YYYY"
    );

  if (appointmentMode.dateConfig.range.dateTo)
    dateTo = dayjs(appointmentMode.dateConfig.range.dateTo).format(
      "DD/MM/YYYY"
    );

  if (appointmentMode.toSearch) toSearch = appointmentMode.toSearch;

  if (appointmentMode.scheduling) scheduling = appointmentMode.scheduling;

  let payload = {
    order: order,
    orderField: orderField,
    page: page,
    size: size,
    kind: null,
    state: state,
    dateToUse: dateToUse,
    dateFrom: dateFrom,
    dateTo: dateTo,
    toSearch: toSearch,
    scheduling: scheduling,
    serviceId: null,
    acceptedToWork: acceptedToWork
  };
  return payload;
}

function getVerifyAppointmentDeliveryDateResult(response, appointments) {
  if (Object.keys(appointments).length > 0) {
    for (let [key, value] of Object.entries(appointments)) {
      response = response.map(a =>
        a.appointmentId === key
          ? {
              ...a,
              appointmentScheduleDate: value.appointmentScheduleDate,
              coveredByFund: value.coveredByFund,
              doctorName: value.doctorName,
              doctorSurname: value.doctorSurname,
              iupCode: value.iupCode,
              memberName: value.memberName,
              memberSurname: value.memberSurname,
              price: value.price,
              protocolCode: value.protocolCode,
              serviceName: value.serviceName,
              invoiceNumber: value.invoice.number,
              invoiceDate: value.invoice.iDate,
              invoiceTotal: value.invoice.total
            }
          : a
      );
    }
  }
  let result = {};
  result["DATEOUTSIDERANGE"] = response.filter(
    app => app.dateCheckResult === false
  );
  result["OK"] = response.filter(
    app =>
      app.massimaleCheckResult === "OK" &&
      app.coperturaCheckResult === true &&
      app.dateCheckResult === true
  );
  result["PARZIALE"] = response.filter(
    app =>
      app.massimaleCheckResult === "PARZIALE" &&
      app.coperturaCheckResult === true &&
      app.dateCheckResult === true
  );
  result["KO"] = response.filter(
    app =>
      (app.coperturaCheckResult === false ||
        app.massimaleCheckResult === "ESAURITO") &&
      app.dateCheckResult === true
  );
  return result;
}

function groupBookableMonthsByCodicePacchetto(bookableMonths) {
  if (bookableMonths?.length === 0) {
    return [];
  }
  let firstMonth = bookableMonths.slice(0, 1)[0];
  let startYear = firstMonth.year;
  let startMonth =
    firstMonth.month.length === 1 ? `0${firstMonth.month}` : firstMonth.month;
  let lastMonth = bookableMonths.slice(-1)[0];
  let endYear = lastMonth.year;
  let endMonth =
    lastMonth.month.length === 1 ? `0${lastMonth.month}` : lastMonth.month;
  let from = dayjs(`${startYear}-${startMonth}-01`);
  let to = dayjs(`${endYear}-${endMonth}-01`);
  let months = [];
  let monthDate = from;
  while (monthDate.isSame(to) || monthDate.isBefore(to)) {
    let searchedMonths = bookableMonths.filter(
      coverage =>
        monthDate.year() === coverage.year &&
        monthDate.month() === coverage.month - 1
    );
    if (searchedMonths.length > 0) {
      let result = searchedMonths.reduce((acc, curr) => {
        acc.bookableByRule = curr.bookableByRule;
        acc.codicePacchetto = curr.codicePacchetto;
        acc.healthCareResidual = curr.healthCareResidual;
        acc.isBookable = curr.isBookable;
        acc.maxCeilingReached = curr.maxCeilingReached;
        acc.maxHealthCareReached = curr.maxHealthCareReached;
        acc.month = curr.month;
        acc.notCovered = curr.notCovered;
        acc.year = curr.year;
        return acc;
      }, {});
      months.push(result);
    } else {
      months.push({
        bookableByRule: false,
        codicePacchetto: [],
        healthCareResidual: 0,
        isBookable: false,
        maxCeilingReached: true,
        maxHealthCareReached: true,
        month: monthDate.month() + 1,
        notCovered: true,
        year: monthDate.year()
      });
    }
    monthDate = monthDate.add(1, "month");
  }
  // fondoest/issues#1098
  months.forEach(m => {
    if (m.codicePacchetto.includes("U1")) {
      m.codicePacchetto = m.codicePacchetto.filter(cp => cp !== "U1");
    }
    if (m.codicePacchetto.includes("MH")) {
      m.codicePacchetto = m.codicePacchetto.filter(cp => cp !== "MH");
    }
  });
  return months;
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
